// Bonus Tracker Utility for Bingo Game
// Handles bonus checking and saving to reports system

// Import apiService for consistent API handling
import apiService from './apiService';

// Bonus types and their prize structures
export const BONUS_TYPES = {
  ONE_CLOSE: "አንድ ዝግ",
  TWO_CLOSE: "ሁለት ዝግ", 
  CHEERS: "ቺርስ"
};

// Prize tables for different bonus types
export const BONUS_PRIZES = {
  [BONUS_TYPES.ONE_CLOSE]: {
    4: 2000,  // 4 calls or less
    5: 200,   // 5 calls
    6: 100,   // 6 calls
    default: 0
  },
  [BONUS_TYPES.TWO_CLOSE]: {
    11: 1000, // 11 calls or less
    12: 500,  // 12 calls
    13: 300,  // 13 calls
    14: 200,  // 14 calls
    15: 100,  // 15 calls
    default: 0
  },
  [BONUS_TYPES.CHEERS]: {
    default: 100 // Fixed prize for CHEERS
  }
};

/**
 * Calculate prize amount based on bonus type and call count
 */
export const calculateBonusPrize = (bonusType, callsCount) => {
  const prizeTable = BONUS_PRIZES[bonusType];
  if (!prizeTable) return 0;

  if (bonusType === BONUS_TYPES.CHEERS) {
    return prizeTable.default;
  }

  // Find the appropriate prize tier
  const sortedTiers = Object.keys(prizeTable)
    .filter(key => key !== 'default')
    .map(Number)
    .sort((a, b) => a - b);

  for (const tier of sortedTiers) {
    if (callsCount <= tier) {
      return prizeTable[tier];
    }
  }

  return prizeTable.default;
};

/**
 * Check if a line (array of numbers) is complete
 */
export const isLineComplete = (line, drawnNumbers) => {
  return line.every(num => {
    if (String(num).toLowerCase() === "free" || num === "0" || num === 0) {
      return true;
    }
    return drawnNumbers.includes(parseInt(num));
  });
};

/**
 * Check for One Close bonus (any single line completed in 4-6 calls)
 */
export const checkOneCloseBonus = (cartella, drawnNumbers) => {
  if (!cartella || !cartella.numbers || drawnNumbers.length > 6) {
    return null;
  }

  // Check rows
  for (let i = 0; i < 5; i++) {
    const row = cartella.numbers[i];
    if (isLineComplete(row, drawnNumbers)) {
      return {
        type: "row",
        lineNumber: i + 1,
        completedNumbers: row.filter(num => num !== "free"),
        callsCount: drawnNumbers.length,
        prize: calculateBonusPrize(BONUS_TYPES.ONE_CLOSE, drawnNumbers.length)
      };
    }
  }

  // Check columns
  for (let j = 0; j < 5; j++) {
    const column = cartella.numbers.map(row => row[j]);
    if (isLineComplete(column, drawnNumbers)) {
      return {
        type: "column",
        lineNumber: j + 1,
        completedNumbers: column.filter(num => num !== "free"),
        callsCount: drawnNumbers.length,
        prize: calculateBonusPrize(BONUS_TYPES.ONE_CLOSE, drawnNumbers.length)
      };
    }
  }

  // Check main diagonal
  const mainDiagonal = cartella.numbers.map((row, i) => row[i]);
  if (isLineComplete(mainDiagonal, drawnNumbers)) {
    return {
      type: "mainDiagonal",
      completedNumbers: mainDiagonal.filter(num => num !== "free"),
      callsCount: drawnNumbers.length,
      prize: calculateBonusPrize(BONUS_TYPES.ONE_CLOSE, drawnNumbers.length)
    };
  }

  // Check anti-diagonal
  const antiDiagonal = cartella.numbers.map((row, i) => row[4 - i]);
  if (isLineComplete(antiDiagonal, drawnNumbers)) {
    return {
      type: "antiDiagonal",
      completedNumbers: antiDiagonal.filter(num => num !== "free"),
      callsCount: drawnNumbers.length,
      prize: calculateBonusPrize(BONUS_TYPES.ONE_CLOSE, drawnNumbers.length)
    };
  }

  // Check 4 corner squares
  const fourCorners = [
    cartella.numbers[0][0], // Top-left
    cartella.numbers[0][4], // Top-right
    cartella.numbers[4][0], // Bottom-left
    cartella.numbers[4][4]  // Bottom-right
  ];
  if (isLineComplete(fourCorners, drawnNumbers)) {
    return {
      type: "fourCorners",
      completedNumbers: fourCorners.filter(num => num !== "free"),
      callsCount: drawnNumbers.length,
      prize: calculateBonusPrize(BONUS_TYPES.ONE_CLOSE, drawnNumbers.length)
    };
  }

  return null;
};

/**
 * Check for Two Close bonus (any two lines completed in 11-15 calls)
 */
export const checkTwoCloseBonus = (cartella, drawnNumbers) => {
  if (!cartella || !cartella.numbers || drawnNumbers.length < 11 || drawnNumbers.length > 15) {
    return null;
  }

  const completedLines = [];

  // Check rows
  for (let i = 0; i < 5; i++) {
    const row = cartella.numbers[i];
    if (isLineComplete(row, drawnNumbers)) {
      completedLines.push(`Row ${i + 1}`);
    }
  }

  // Check columns
  for (let j = 0; j < 5; j++) {
    const column = cartella.numbers.map(row => row[j]);
    if (isLineComplete(column, drawnNumbers)) {
      completedLines.push(`Column ${j + 1}`);
    }
  }

  // Check diagonals
  const mainDiagonal = cartella.numbers.map((row, i) => row[i]);
  if (isLineComplete(mainDiagonal, drawnNumbers)) {
    completedLines.push("Main diagonal");
  }

  const antiDiagonal = cartella.numbers.map((row, i) => row[4 - i]);
  if (isLineComplete(antiDiagonal, drawnNumbers)) {
    completedLines.push("Anti-diagonal");
  }

  // Check 4 corner squares
  const fourCorners = [
    cartella.numbers[0][0], // Top-left
    cartella.numbers[0][4], // Top-right
    cartella.numbers[4][0], // Bottom-left
    cartella.numbers[4][4]  // Bottom-right
  ];
  if (isLineComplete(fourCorners, drawnNumbers)) {
    completedLines.push("Four corners");
  }

  if (completedLines.length >= 2) {
    return {
      completedLines: completedLines.slice(0, 2),
      totalLinesFound: completedLines.length,
      callsCount: drawnNumbers.length,
      prize: calculateBonusPrize(BONUS_TYPES.TWO_CLOSE, drawnNumbers.length)
    };
  }

  return null;
};

/**
 * Check for CHEERS bonus (all 5 CHEERS numbers found in cartella)
 */
export const checkCheersBonus = (cartella, drawnNumbers, cheersNumbers) => {
  if (!cartella || !cartella.numbers || !cheersNumbers || cheersNumbers.length !== 5) {
    return null;
  }

  const targetNumbers = cheersNumbers.map(item => item.number);
  const foundNumbers = [];

  for (const targetNum of targetNumbers) {
    let found = false;

    // Check if number is in cartella and has been drawn
    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 5; j++) {
        const num = cartella.numbers[i][j];
        if (
          num !== "free" &&
          parseInt(num) === targetNum &&
          drawnNumbers.includes(targetNum)
        ) {
          found = true;
          foundNumbers.push(targetNum);
          break;
        }
      }
      if (found) break;
    }
  }

  if (foundNumbers.length === 5) {
    return {
      foundNumbers: foundNumbers,
      cheersNumbers: targetNumbers,
      callsCount: drawnNumbers.length,
      prize: calculateBonusPrize(BONUS_TYPES.CHEERS, drawnNumbers.length)
    };
  }

  return null;
};

/**
 * Check all bonus types for a given cartella
 */
export const checkAllBonuses = (cartella, drawnNumbers, cheersNumbers) => {
  const bonuses = {};

  // Check One Close bonus
  const oneClose = checkOneCloseBonus(cartella, drawnNumbers);
  if (oneClose && oneClose.prize > 0) {
    bonuses.oneClose = oneClose;
  }

  // Check Two Close bonus
  const twoClose = checkTwoCloseBonus(cartella, drawnNumbers);
  if (twoClose && twoClose.prize > 0) {
    bonuses.twoClose = twoClose;
  }

  // Check CHEERS bonus
  const cheers = checkCheersBonus(cartella, drawnNumbers, cheersNumbers);
  if (cheers && cheers.prize > 0) {
    bonuses.cheers = cheers;
  }

  return bonuses;
};

/**
 * Save bonus data to the reports system
 */
export const saveBonusToReports = async (bonusData) => {
  try {
    const response = await apiService.post('reports/save-round', {
      // Required report fields (using defaults for bonus-only reports)
      round: 0,
      price: 0,
      noPlayer: 0,
      winnerPrice: 0,
      income: 0,
      date: new Date().toISOString(),
      branchId: null, // Will be handled by backend based on user
      // Bonus data
      bonusType: bonusData.bonusType,
      bonusAmount: bonusData.bonusAmount,
      bonusDetails: JSON.stringify(bonusData.bonusDetails)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to save bonus to reports');
    }

    const result = await response.json();
    return result;

  } catch (error) {
    console.error('❌ Error saving bonus to reports:', error);
    throw error;
  }
};

/**
 * Get bonus statistics from reports
 */
export const getBonusReports = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.bonusType) queryParams.append('bonusType', filters.bonusType);

    const endpoint = `reports/bonus${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await apiService.get(endpoint);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch bonus reports');
    }

    const result = await response.json();
    return result;

  } catch (error) {
    console.error('❌ Error fetching bonus reports:', error);
    throw error;
  }
};

export default {
  BONUS_TYPES,
  BONUS_PRIZES,
  calculateBonusPrize,
  isLineComplete,
  checkOneCloseBonus,
  checkTwoCloseBonus,
  checkCheersBonus,
  checkAllBonuses,
  saveBonusToReports,
  getBonusReports
};
